// src/encounter/lib/line-piece-text.ts
//
// PROD-2824 / PROD-2961 · frame v2 only. How a PIECE reads in a plain-text note LINE (an HPI, ROS, PE or
// Risk narrative, or a free-text line). A pick-list and a blank read as in the A&P note (`pieceDisplay`).
// A chart link reads differently (design desk, 5 Oct: one label, one "as of", D-45 time): the phrase's own
// words name the chart ("Allergies as recorded: "), so the link adds its VALUES and the stamp ONCE, after
// them — "Allergies as recorded: Penicillin (criticality: high); Sulfa (as of 5 Oct 2026, 22:00)" — never
// "Allergies as recorded: Allergies (as of …)". The occurrence shows the values alone under a header that
// carries the "as of". The A&P note keeps its own chart wording (note-pieces.ts); this is the line's.
//
// Used by the occurrence (the prose it completes) AND by the line's saved Delta (note-line-delta.ts), so
// the text and the pieces kept beside it always agree.
import { formatDateTimeD45 } from '@/shared/lib/date-format';
import { isPieceOpen, pieceDisplay, pieceLabel, type ChartPiece, type Piece } from './note-pieces';

/** The time a stamp reads in, D-45 ("5 Oct 2026, 22:00"). */
export function stampText(iso: string | undefined): string {
  return formatDateTimeD45(iso) || 'unknown time';
}

/** A chart link's values as the occurrence shows them under its header: the result lines, or its honest
 *  state (R6) — never the chart's name again, never a second "as of". */
export function chartValueText(piece: ChartPiece): string {
  const s = piece.snapshot;
  if (!s || s.status === 'loading') return 'Loading…';
  if (s.status === 'error') return 'Could not be loaded';
  if (s.status === 'empty') return `No ${pieceLabel(piece).toLowerCase()} recorded`;
  return s.results.map((r) => r.display).join('; ');
}

/** What a LINE receives for a RESOLVED chart link: its values, then the stamp once (with when the newest
 *  value was measured, when the chart says). An unresolved link keeps its placeholder wording — which
 *  Complete never lets into a line. */
export function chartLineText(piece: ChartPiece): string {
  const s = piece.snapshot;
  if (!s || isPieceOpen(piece)) return pieceDisplay(piece);
  const asOf = `as of ${stampText(s.pulledAt)}`;
  const measured = s.results.map((r) => r.measuredAt).filter(Boolean).sort().pop();
  return `${chartValueText(piece)} (${measured ? `measured ${stampText(measured)}; ${asOf}` : asOf})`;
}

/** A piece's text in a line: a chart link in the line's wording, anything else as the A&P note shows it. */
export function lineDisplay(piece: Piece): string {
  return piece.t === 'chart' ? chartLineText(piece) : pieceDisplay(piece);
}
